/**
 * Primary Key Processor for V2 Templates
 *
 * Handles two types of primary key specifications:
 * 1. JSONPath extraction: "$.primary_keys<array>" - extract from discovery data
 * 2. Spec processing rules: computed values using operations like concat, etc.
 */

export interface JsonPathSpec {
  type: 'jsonpath';
  spec: string; // e.g., "$.primary_keys<array>"
}

export interface ProcessingRuleSpec {
  type: 'computed';
  rule: {
    operation: 'concat' | 'join' | 'transform';
    parts: Array<{
      type: 'variable' | 'literal' | 'jsonpath';
      value: string;
    }>;
  };
}

export type PrimaryKeySpec = JsonPathSpec | ProcessingRuleSpec;

/**
 * Process primary keys based on the specification
 * @param feedData - The raw feed data from discovery
 * @param primaryKeySpec - The primary key specification
 * @returns Array of processed primary key strings
 */
export function processPrimaryKeySpec(feedData: any, primaryKeySpec: PrimaryKeySpec): string[] {
  if (!primaryKeySpec) {
    return [];
  }

  switch (primaryKeySpec.type) {
    case 'jsonpath':
      return processJsonPathSpec(feedData, primaryKeySpec.spec);

    case 'computed':
      return processComputedSpec(feedData, primaryKeySpec.rule);

    default:
      console.warn('Unknown primary key spec type:', primaryKeySpec);
      return [];
  }
}

/**
 * Process JSONPath specification
 * @param feedData - The raw feed data
 * @param spec - JSONPath spec like "$.primary_keys<array>"
 * @returns Array of primary key strings
 */
function processJsonPathSpec(feedData: any, spec: string): string[] {
  if (!spec || typeof spec !== 'string') {
    return [];
  }

  try {
    // Parse jsonpath<data-type> format
    const match = spec.match(/^(.+)<([^>]+)>$/);
    if (!match) {
      // Simple string, return as single-item array
      return [spec];
    }

    const [, jsonpath, dataType] = match;
    const extractedValue = extractJsonPathValue(feedData, jsonpath.trim());

    // Cast to specified data type
    const castedValue = castToDataType(extractedValue, dataType.trim());

    // Ensure we return an array of strings
    if (Array.isArray(castedValue)) {
      return castedValue.map((pk) => String(pk));
    } else {
      return [String(castedValue)];
    }
  } catch (error) {
    console.warn('Failed to process JSONPath spec:', spec, 'Error:', error.message);
    // Return empty array instead of throwing to allow other processing to continue
    return [];
  }
}

/**
 * Process computed specification using processing rules
 * @param feedData - The raw feed data
 * @param rule - The processing rule
 * @returns Array of primary key strings
 */
function processComputedSpec(feedData: any, rule: ProcessingRuleSpec['rule']): string[] {
  if (!rule || !rule.parts) {
    return [];
  }

  switch (rule.operation) {
    case 'concat':
      return processConcatOperation(feedData, rule.parts);

    case 'join':
      return processJoinOperation(feedData, rule.parts);

    case 'transform':
      return processTransformOperation(feedData, rule.parts);

    default:
      console.warn('Unknown processing rule operation:', rule.operation);
      return [];
  }
}

/**
 * Process concatenation operation
 * @param feedData - The raw feed data
 * @param parts - Array of parts to concatenate
 * @returns Array of primary key strings
 */
function processConcatOperation(
  feedData: any,
  parts: Array<{ type: string; value: string }>,
): string[] {
  const result = parts
    .map((part) => {
      switch (part.type) {
        case 'variable':
          // Handle variable substitution like {feed_name}
          return substituteVariable(feedData, part.value);

        case 'literal':
          // Use literal value as-is
          return part.value;

        case 'jsonpath':
          // Extract value using JSONPath
          return extractJsonPathValue(feedData, part.value);

        default:
          console.warn('Unknown part type in concat operation:', part.type);
          return '';
      }
    })
    .join('');

  return [result];
}

/**
 * Process join operation (for multiple primary keys)
 * @param feedData - The raw feed data
 * @param parts - Array of parts to join
 * @returns Array of primary key strings
 */
function processJoinOperation(
  feedData: any,
  parts: Array<{ type: string; value: string }>,
): string[] {
  // For join operation, we want to create separate primary keys from each part
  // But if parts are meant to be concatenated, they should use concat operation
  // This implementation creates separate primary keys from each part
  return parts
    .map((part) => {
      switch (part.type) {
        case 'variable':
          return substituteVariable(feedData, part.value);

        case 'literal':
          return part.value;

        case 'jsonpath':
          return extractJsonPathValue(feedData, part.value);

        default:
          console.warn('Unknown part type in join operation:', part.type);
          return '';
      }
    })
    .filter((pk) => pk); // Remove empty strings
}

/**
 * Process transform operation (for complex transformations)
 * @param feedData - The raw feed data
 * @param parts - Array of parts to transform
 * @returns Array of primary key strings
 */
function processTransformOperation(
  feedData: any,
  parts: Array<{ type: string; value: string }>,
): string[] {
  // For now, treat transform as join - can be extended for more complex operations
  return processJoinOperation(feedData, parts);
}

/**
 * Substitute variables in the format {variable_name}
 * @param feedData - The raw feed data
 * @param value - The value containing variables
 * @returns Substituted string
 */
function substituteVariable(feedData: any, value: string): string {
  if (!value || typeof value !== 'string') {
    return '';
  }

  // Handle variable substitution like {feed_name}
  return value.replace(/\{([^}]+)\}/g, (match, variableName) => {
    // Common variable substitutions
    switch (variableName) {
      case 'feed_name':
        return feedData.feed_name || feedData.name || '';
      case 'feed_display_name':
        return feedData.feed_display_name || feedData.display_name || feedData.name || '';
      default:
        // Try to get from feedData
        return feedData[variableName] || match;
    }
  });
}

/**
 * Extract value using JSONPath
 * @param data - The data object
 * @param jsonpath - The JSONPath expression
 * @returns Extracted value
 */
function extractJsonPathValue(data: any, jsonpath: string): any {
  if (!data || !jsonpath) {
    return null;
  }

  // Handle simple JSONPath expressions
  if (jsonpath === '$') {
    return data;
  } else if (jsonpath.startsWith('$.')) {
    const path = jsonpath.substring(2); // Remove "$."
    return getNestedValue(data, path);
  } else {
    // For more complex JSONPath expressions, you'd need a proper JSONPath library
    console.warn('Complex JSONPath expressions not yet supported:', jsonpath);
    return null;
  }
}

/**
 * Get nested value from object using dot notation
 * @param obj - The object to traverse
 * @param path - Dot-separated path (e.g., "tables[0].name")
 * @returns The value at the path or undefined
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) {
    return undefined;
  }

  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }

    // Handle array access like "tables[0]"
    const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, arrayName, index] = arrayMatch;
      current = current[arrayName];
      if (Array.isArray(current)) {
        current = current[parseInt(index, 10)];
      } else {
        return undefined;
      }
    } else {
      current = current[part];
    }
  }

  return current;
}

/**
 * Cast value to specified data type
 * @param value - The value to cast
 * @param dataType - The target data type
 * @returns Cast value
 */
function castToDataType(value: any, dataType: string): any {
  if (value === null || value === undefined) {
    return null;
  }

  switch (dataType.toLowerCase()) {
    case 'string':
      return String(value);
    case 'number':
    case 'int':
    case 'integer':
      return Number(value);
    case 'boolean':
    case 'bool':
      return Boolean(value);
    case 'array':
      return Array.isArray(value) ? value : [value];
    case 'object':
      return typeof value === 'object' ? value : { value };
    default:
      console.warn('Unknown data type for casting:', dataType);
      return value;
  }
}

/**
 * Legacy function for backward compatibility
 * @param feedData - The feed data
 * @param primaryKeysConfig - The primary keys configuration (string, array, or spec object)
 * @returns Array of primary key strings
 */
export function processPrimaryKeys(feedData: any, primaryKeysConfig: any): string[] {
  if (!primaryKeysConfig) {
    return [];
  }

  // If it's already an array, return it
  if (Array.isArray(primaryKeysConfig)) {
    return primaryKeysConfig.map((pk) => String(pk));
  }

  // If it's a string, check if it's a jsonpath<type> format
  if (typeof primaryKeysConfig === 'string') {
    const match = primaryKeysConfig.match(/^(.+)<([^>]+)>$/);
    if (match) {
      // It's a jsonpath<type> format
      return processJsonPathSpec(feedData, primaryKeysConfig);
    } else {
      // It's a simple string, return as single-item array
      return [primaryKeysConfig];
    }
  }

  // If it's an object, treat it as a spec
  if (typeof primaryKeysConfig === 'object') {
    return processPrimaryKeySpec(feedData, primaryKeysConfig as PrimaryKeySpec);
  }

  // Fallback: convert to string and return as single-item array
  return [String(primaryKeysConfig)];
}
